import { __getEntityInitializeInternalKey, Entity } from './Entity.js';
import { Component} from './Component.js';
import { System } from './base/System.js';

const ENTITY_INITIALIZE_INTERNAL_KEY = __getEntityInitializeInternalKey();

/**
 * Entity constructor type.
 */
export interface IEntity {
  new (...args: Array<any>): Entity
}

/**
 * Manages entity lifecycle and system hooks.
 */
export class EntityManager {
  private systemLookup: {[system: string]: System};
  readonly $api: Record<string, any>;
  readonly $gameData : any;

  constructor(
    systemLookup: {[system: string]: System}, 
    $api: Record<string, any>, 
    $gameData: any
  ) {
    this.systemLookup = systemLookup;
    this.$api = $api;
    this.$gameData = $gameData;
  }

  /**
   * decorates the entity with hooks for system onRemove and onAdded;
   * @param entity
   * @param data - any additional data you may want to use to initialize the entity inside of the system hooks
   */
  public initializeEntity<InitType>(entity: Entity<InitType>, data?: InitType) {
    entity.$api = this.$api;
    entity.$gameData = this.$gameData;
    entity.$initializeEntity = this.initializeEntity.bind(this);
    entity.$destroyEntity = this.destroyEntity.bind(this);
    
    const oldAddComponent = entity.addComponent;
    entity.addComponent = (component: Component) => {
      oldAddComponent.call(entity, component);
      const system = this.systemLookup[component.name];
      if(system) {
        component.$api = system.$api;
        component.$gameData = system.$gameData;
        system.__trackEntity(entity, component);
        system.onEntityAddedComponent?.(entity, component);
      }
    };

    const oldRemoveComponent = entity.removeComponent;
    entity.removeComponent = (componentName: string, options?: any) => {
      this.systemLookup[componentName]?.handleEntityRemovedComponent(entity, entity.getComponent(componentName), options);
      oldRemoveComponent.call(entity, componentName, options);
    };

    entity.__initializeForSystem(data, ENTITY_INITIALIZE_INTERNAL_KEY);
    return entity;
  }

  public destroyEntity(entity: Entity, options?: any) {
    delete entity.$api;
    delete entity.$gameData;
    entity.destroy(options);
  }
}